import { extendTheme } from "@chakra-ui/react";
import colors from "./colors";
import { components } from "./components";
import { layerStyles } from "./layer-styles";
import { space, sizes } from "./scale";
import { textStyles } from "./text-styles";
import { remCalc } from "./utils";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const styles = {
  global: {
    body: {
      color: "brand.text.light",
      textRendering: "optimizeLegibility",
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    },
  },
};

const fonts = {
  heading: `'Nuckle', sans-serif`,
  body: `'Nuckle', sans-serif`,
  inter: `'Inter', sans-serif`,
  mono: `'Roboto Mono', monospace`,
};

const fontSizes = {
  brand: {
    "2xs": remCalc(9.17),
    xs: remCalc(11),
    sm: remCalc(13.2),
    md: remCalc(17.4),
    lg: remCalc(19.01),
    xl: remCalc(22.81),
    "2xl": remCalc(27.37),
    "3xl": remCalc(32.85),
    "4xl": remCalc(39.42),
    "5xl": remCalc(47.3),
    "6xl": remCalc(56.76),
    "7xl": remCalc(68.11),
    "8xl": remCalc(81.73),
  },
  // -----------------------------------
  // Legacy font sizes
  // -----------------------------------
  xs: "12px",
  sm: "14px",
  md: "16px",
  lg: "18px",
  xl: "22px",
  "2xl": "26px",
  "3xl": "32px",
  "4xl": "40px",
  "5xl": "46px",
  "6xl": "54px",
  "7xl": "64px",
};

const lineHeights = {
  "12": remCalc(12),
  "14": remCalc(14),
  "16": remCalc(16),
  "20": remCalc(20),
  "24": remCalc(24),
  "28": remCalc(28),
  "32": remCalc(32),
  "40": remCalc(40),
  "48": remCalc(48),
  "56": remCalc(56),
  "64": remCalc(64),
  "72": remCalc(72),
  "84": remCalc(84),
};

const shadows = {
  base: "0px 2px 12px 0px rgba(0, 0, 0, 0.08)", // XX: Legacy style
  xs: "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)",
  sm: "0px 1.885px 1.885px 0px rgba(0, 0, 0, 0.02), 0px 3.77px 3.77px 0px rgba(0, 0, 0, 0.04), 0px 11.31px 22.621px 0px rgba(0, 0, 0, 0.04)",
  md: "0px 34.807px 20.627px 0px rgba(0, 0, 0, 0.02), 0px 15.47px 15.47px 0px rgba(0, 0, 0, 0.04), 0px 3.867px 7.735px 0px rgba(0, 0, 0, 0.05)",
  lg: "0px 29px 38px 0px rgba(0, 0, 0, 0.07), 0px 14.681px 16.566px 0px rgba(0, 0, 0, 0.05), 0px 5.8px 6.175px 0px rgba(0, 0, 0, 0.04), 0px 1.269px 2.197px 0px rgba(0, 0, 0, 0.02)",
  xl: " 0px 100px 80px 0px rgba(0, 0, 0, 0.15), 0px 64.815px 46.852px 0px rgba(0, 0, 0, 0.11), 0px 38.519px 25.481px 0px rgba(0, 0, 0, 0.09), 0px 20px 13px 0px rgba(0, 0, 0, 0.08), 0px 8.148px 6.519px 0px rgba(0, 0, 0, 0.06), 0px 1.852px 3.148px 0px rgba(0, 0, 0, 0.04)",
  card: "0px -1px 6px 0px rgba(0, 0, 0, 0.05), 0px 12px 12px 0px rgba(0, 0, 0, 0.04), 0px 27px 16px 0px rgba(0, 0, 0, 0.02), 0px 47px 19px 0px rgba(0, 0, 0, 0.01)",
  linkCardOutline:
    "0px 0px 0px 2px #40DE9E, 0px -1px 6px 0px rgba(0, 0, 0, 0.05), 0px 12px 12px 0px rgba(0, 0, 0, 0.04), 0px 27px 16px 0px rgba(0, 0, 0, 0.02), 0px 47px 19px 0px rgba(0, 0, 0, 0.01)",
  outline: "0px 0px 0px 2px #40DE9E",
  outlineRed: "0px 0px 0px 2px #F85D3E",
  // When elements with box shadows move up on the y axis on hover, the shadow should become larger and diffuse to mirror real lighting.
  // IMPORTANT: Never animate the box-shadow property, it will have a major performance impact.
  // Read more here: https://tobiasahlin.com/blog/how-to-animate-box-shadow/
  hoverTranslateY: {
    md: "0px 54.807px 40.627px 0px rgba(0, 0, 0, 0.02), 0px 35.47px 25.47px 0px rgba(0, 0, 0, 0.04), 0px 23.867px 17.735px 0px rgba(0, 0, 0, 0.05)",
    card: "0px 7px 16px 0px rgba(0, 0, 0, 0.05), 0px 20px 22px 0px rgba(0, 0, 0, 0.04), 0px 35px 26px 0px rgba(0, 0, 0, 0.02), 0px 55px 29px 0px rgba(0, 0, 0, 0.01)",
  },
};

const radii = {
  ...space,
  base: space.smaller,
  full: "9999px",
};

export const theme = extendTheme({
  config,
  styles,
  colors,
  fonts,
  fontSizes,
  textStyles,
  layerStyles,
  lineHeights,
  space,
  sizes,
  radii,
  shadows,
  components,
});
